/* ---------------------------------------------------------------------------
   The elements a bridge is inspected in parts.

   PROVENANCE — read this before adding to the list.

   These are the eleven items of DoR's own **Routine and Principal Inspection
   forms**, in the order the forms print them: *Guidelines for Inspection and
   Maintenance of Bridges, Volume 1, Part 2 — Bridge Inspection Manual*, §5.2
   and Appendix C. Items 1–10 are also, exactly, the rows of the **Bridge
   Inspection Summary** sheet that the Divisional Engineer signs; item 11
   (culverts) is a form used when the structure is one.

   **This list used to be something else, and the swap was a promise this file
   made.** Until `QC-09` it was the components named in the *Construction
   Details* block of DoR's bridge-inventory sheet, and the header here said:

   > If the *Guideline for Inspection and Maintenance of Bridge* names a
   > different or longer set — wing walls, approach slabs, deck drainage — that
   > guideline wins and this list should be replaced wholesale rather than
   > extended by guesswork.

   It does name one. This is it. The inventory sheet describes what a structure
   is *made of*; the inspection form names what an inspection *rates*, and those
   are different questions — which is why the sheet's list could not carry an
   approach road, an expansion joint or a river-training work, all four of which
   an inspector is required to rate and none of which is a construction detail.

   **`sheetField` is now the exception rather than the rule.** Where an item
   corresponds to a construction field the register already carries, it is named,
   because that link is useful and was checked. Where it does not — an approach
   road is not a component the inventory sheet describes — there is no field, and
   inventing one would be the guesswork the old header warned about.

   Ids are stable and stored in D1 inside the inspection's JSON. Renaming a
   `label` is free; changing an `id` is a migration — see RETIRED_ELEMENTS below
   for what happened to the previous vocabulary's.
   --------------------------------------------------------------------------- */

/**
 * Where an element sits on the form. Groups the breakdown on screen.
 *
 * These are the form's own headings, not a structural taxonomy. "Ancillary
 * works" carries the deck surface and the parapet because that is the block they
 * are printed under — an inspector holding the paper should find them where the
 * paper puts them, and a tidier grouping invented here would be a second
 * vocabulary to translate.
 */
export type ElementGroup =
  | 'Ancillary works'
  | 'Superstructure'
  | 'Substructure'
  | 'Training works'
  | 'Other';

export interface BridgeElement {
  /** Stable id, stored. Never change one without a migration. */
  id: string;
  /** What the inspection form calls it, near enough to be recognised by someone holding one. */
  label: string;
  group: ElementGroup;
  /**
   * The item number printed on the Bridge Inspection Summary, 1–10.
   *
   * Absent for culverts, which has an inspection form but no summary row —
   * a structure that is a culvert is not one of the ten parts of a bridge.
   * `QC-07` prints the sheet in this order and refuses to invent a row for
   * anything without a number.
   */
  formItem?: number;
  /** Disambiguation for a label the form keeps short. */
  hint?: string;
  /**
   * The field on `Bridge` that records this item's construction, where there is
   * one. Not a link the code follows — it is here so that anyone checking the
   * list against the inventory sheet can see which row each came from.
   */
  sheetField?: string;
}

/**
 * Every element an inspection can rate, in the order the form prints them.
 *
 * Eleven, not twelve. The previous list separated foundations from the things
 * standing on them — `pier` and `pier_foundation` — on the argument that scour is
 * the dominant Nepali failure mode and "the pier is fine, its foundation is not"
 * is the sentence one rating cannot say. **That sentence is still sayable and is
 * said better**: DoR puts scour under item 9 (bridge protection works) and item
 * 10 (river training), which are the works that fail when a foundation is
 * undermined, and a `SCO` defect now carries a measured scour depth against the
 * pier itself (see src/data/quantities.ts). The finding moved to where the form
 * and the measurement both put it, rather than being lost.
 */
export const BRIDGE_ELEMENTS: readonly BridgeElement[] = [
  {
    id: 'approach',
    label: 'Approach roads, embankments, drainage',
    group: 'Ancillary works',
    formItem: 1,
    hint: 'The road either side, its embankments and their drainage',
  },
  {
    id: 'deck_surface',
    label: 'Bridge deck surface, drainage and footpath',
    group: 'Ancillary works',
    formItem: 2,
    hint: 'The running surface and what drains it — not the structural deck, which is item 5',
    sheetField: 'runningSurface',
  },
  {
    id: 'parapet',
    label: 'Parapet, railing, guardrail and signs',
    group: 'Ancillary works',
    formItem: 3,
    sheetField: 'railings',
  },
  {
    id: 'joints',
    label: 'Expansion joints',
    group: 'Ancillary works',
    formItem: 4,
    sheetField: 'movement',
  },
  {
    id: 'deck',
    label: 'Deck',
    group: 'Superstructure',
    formItem: 5,
    hint: 'The structural deck — concrete, steel composite, steel truss, timber, masonry arch or Bailey',
    sheetField: 'type',
  },
  {
    id: 'bearings',
    label: 'Bearings',
    group: 'Superstructure',
    formItem: 6,
    sheetField: 'bearings',
  },
  {
    id: 'abutment',
    label: 'Abutments, wing walls and retaining walls',
    group: 'Substructure',
    formItem: 7,
    sheetField: 'abutmentType',
  },
  { id: 'pier', label: 'Piers', group: 'Substructure', formItem: 8, sheetField: 'pierType' },
  {
    id: 'protection',
    label: 'Bridge protection works',
    group: 'Training works',
    formItem: 9,
    hint: 'Slope and bed protection — pitching, gabions, rip-rap, piled walls',
  },
  {
    id: 'river_training',
    label: 'River training',
    group: 'Training works',
    formItem: 10,
    hint: 'Channel control, bank protection, blockage and change of river path',
    sheetField: 'riverTraining',
  },
  {
    id: 'culverts',
    label: 'Culverts',
    group: 'Other',
    hint: 'Its own form, used where the structure is a culvert — no row on the summary sheet',
  },
];

/**
 * The groups in the order the inspection form prints them.
 *
 * Not the old "top of the structure downwards" order. The form's order is what
 * an inspector filling it in walks, and the summary sheet numbers its rows 1–10
 * along it — a screen that reordered them would make the printed sheet and the
 * screen disagree about which item is item 5.
 */
export const ELEMENT_GROUPS: readonly ElementGroup[] = [
  'Ancillary works',
  'Superstructure',
  'Substructure',
  'Training works',
  'Other',
];

/**
 * What the previous vocabulary's ids meant, for records that carry one.
 *
 * `QC-09` replaced the list wholesale. Every inspection recorded before it went
 * in stores the old ids inside its JSON, and those are facts somebody recorded
 * at a structure — so **nothing rewrites them**. Mapping `pier_foundation` onto
 * `pier` would be the register editing its own history: an inspector who rated a
 * foundation `1` while the pier above it was `3` said two things, and a migration
 * that merged them would leave one number claiming to be the other.
 *
 * So the old ids are not accepted on the way in — `isKnownElement` refuses them,
 * and the dialog cannot produce one — but they still READ, here, under the name
 * they were recorded against. `elementLabel` is what draws them, and
 * `isRetiredElement` is what lets a screen mark them as belonging to the
 * superseded form rather than silently mixing two vocabularies in one column.
 */
export const RETIRED_ELEMENTS: Readonly<Record<string, string>> = {
  abutment_foundation: 'Abutment foundation',
  pier_foundation: 'Pier foundation',
  superstructure: 'Super structure',
  movement: 'Movement',
  running_surface: 'Running surface',
  railings: 'Railings',
  road_signs: 'Road signs',
  services: 'Services',
};

const BY_ID = new Map(BRIDGE_ELEMENTS.map((e) => [e.id, e]));

/** The element with this id, or null for one this build does not rate. */
export function elementById(id: string): BridgeElement | null {
  return BY_ID.get(id) ?? null;
}

/**
 * Items an inspector adds to one inspection beyond the form's own — a sign
 * gantry, a utility bracket, a staircase. The label is carried in the id
 * itself, so a custom item needs no schema of its own and reads back the same
 * way everywhere `elementLabel` is used.
 */
export const CUSTOM_ELEMENT_PREFIX = 'custom:';
export const CUSTOM_ELEMENT_MAX = 80;

export function customElementId(label: string): string {
  return CUSTOM_ELEMENT_PREFIX + label.trim().replace(/\s+/g, ' ');
}

export function isCustomElement(id: string): boolean {
  const label = id.slice(CUSTOM_ELEMENT_PREFIX.length);
  return id.startsWith(CUSTOM_ELEMENT_PREFIX) && label.trim() !== '' && label.length <= CUSTOM_ELEMENT_MAX;
}

/**
 * Label for a stored element id.
 *
 * Three tiers, and the order matters. A current element reads as the form calls
 * it; an id from the superseded vocabulary reads as THAT form called it; and
 * anything else falls back to the raw id rather than hiding the row. An
 * inspection recorded against an element a later build renamed away is still a
 * fact somebody recorded, and dropping it from the screen would be the register
 * quietly editing its own history.
 */
export function elementLabel(id: string): string {
  return (
    BY_ID.get(id)?.label ??
    RETIRED_ELEMENTS[id] ??
    (id.startsWith(CUSTOM_ELEMENT_PREFIX) ? id.slice(CUSTOM_ELEMENT_PREFIX.length) : id)
  );
}

/** Whether an id is one this build recognises — what the API validates against. */
export function isKnownElement(id: string): boolean {
  return BY_ID.has(id);
}

/**
 * Whether an id belongs to the vocabulary `QC-09` superseded.
 *
 * What a screen uses to say so. A breakdown that drew "Pier foundation" beside
 * "Piers" with no mark between them would read as one form with eleven items and
 * a twelfth nobody can find — the mark is what makes the record legible as two
 * vocabularies, one of which is no longer offered.
 */
export function isRetiredElement(id: string): boolean {
  return !BY_ID.has(id) && id in RETIRED_ELEMENTS;
}

/** The summary sheet's rows, 1–10, in printed order. `QC-07` draws exactly these. */
export const SUMMARY_ITEMS: readonly BridgeElement[] = BRIDGE_ELEMENTS.filter(
  (e) => e.formItem !== undefined,
).sort((a, b) => (a.formItem ?? 0) - (b.formItem ?? 0));

/**
 * The elements as a site record offers them: under the form's own headings, and
 * with what a builder calls the parts each one covers (QA 2026-09-30). The form
 * says "Deck" for the whole structural deck, so "Girder erected" had nothing a
 * site agent recognised to hang on. The ids and the form's labels are unchanged;
 * only these pickers read the longer words.
 */
const SITE_LABEL: Readonly<Record<string, string>> = {
  deck: 'Deck: girders, diaphragms and slab',
  pier: 'Piers: piles, footings, columns and caps',
};

export function siteElementGroups(): { group: ElementGroup; elements: { id: string; label: string }[] }[] {
  return ELEMENT_GROUPS.map((group) => ({
    group,
    elements: BRIDGE_ELEMENTS.filter((e) => e.group === group).map((e) => ({ id: e.id, label: SITE_LABEL[e.id] ?? e.label })),
  })).filter((g) => g.elements.length > 0);
}
